// src/components/VisioTab.jsx
import { useEffect, useRef, useState, useCallback } from "react";

export default function VisioTab({
    wsRef,
    addCallListener,
    hangupCall,
    matchId,
    fromUserId,
    toUserId,
    offer,
    onHangup,
    visible = false
}) {
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const pcRef = useRef(null);
    const localStreamRef = useRef(null);
    const pendingCandidatesRef = useRef([]);
    const [inCall, setInCall] = useState(false);
    const [cameraOn, setCameraOn] = useState(true);
    const [micOn, setMicOn] = useState(true);

    // draggable / resizable
    const [position, setPosition] = useState({ x: 180, y: 120 });
    const [size, setSize] = useState({ width: 420, height: 320 });
    const [isFullscreen, setIsFullscreen] = useState(false);
    const dragRef = useRef(null);
    const resizeRef = useRef(null);

    // --- drag / resize handlers ---
    const startDragging = (e) => {
        if (isFullscreen) return;
        dragRef.current = { startX: e.clientX, startY: e.clientY, origX: position.x, origY: position.y };
        window.addEventListener("mousemove", onDrag);
        window.addEventListener("mouseup", stopDragging);
    };
    const onDrag = (e) => {
        if (!dragRef.current) return;
        setPosition({
            x: dragRef.current.origX + (e.clientX - dragRef.current.startX),
            y: dragRef.current.origY + (e.clientY - dragRef.current.startY)
        });
    };
    const stopDragging = () => {
        dragRef.current = null;
        window.removeEventListener("mousemove", onDrag);
        window.removeEventListener("mouseup", stopDragging);
    };
    const startResizing = (e) => {
        if (isFullscreen) return;
        resizeRef.current = { startX: e.clientX, startY: e.clientY, origW: size.width, origH: size.height };
        window.addEventListener("mousemove", onResize);
        window.addEventListener("mouseup", stopResizing);
    };
    const onResize = (e) => {
        if (!resizeRef.current) return;
        setSize({
            width: Math.max(260, resizeRef.current.origW + (e.clientX - resizeRef.current.startX)),
            height: Math.max(180, resizeRef.current.origH + (e.clientY - resizeRef.current.startY))
        });
    };
    const stopResizing = () => {
        resizeRef.current = null;
        window.removeEventListener("mousemove", onResize);
        window.removeEventListener("mouseup", stopResizing);
    };
    const toggleFullscreen = () => {
        if (!isFullscreen) {
            setPosition({ x: 0, y: 0 });
            setSize({ width: window.innerWidth, height: window.innerHeight });
            setIsFullscreen(true);
        } else {
            setPosition({ x: 180, y: 120 });
            setSize({ width: 420, height: 320 });
            setIsFullscreen(false);
        }
    };

    // --- WebRTC helpers ---
    const drainPendingCandidates = useCallback(async () => {
        if (!pcRef.current) return;
        const pending = pendingCandidatesRef.current;
        for (const c of pending) {
            try { await pcRef.current.addIceCandidate(c); } catch (err) { console.warn("addIceCandidate (pending) failed:", err); }
        }
        pendingCandidatesRef.current = [];
    }, []);

    const createPeerConnection = useCallback(async () => {
        if (pcRef.current) return;

        pcRef.current = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });

        pcRef.current.onicecandidate = (event) => {
            if (event.candidate && wsRef?.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                    action: "call_ice_candidate",
                    fromUserId,
                    toUserId,
                    matchId,
                    candidate: event.candidate
                }));
            }
        };

        pcRef.current.ontrack = (event) => {
            console.log("Received remote track:", event.streams);
            if (remoteVideoRef.current) remoteVideoRef.current.srcObject = event.streams[0];
        };

        try {
            localStreamRef.current = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            localStreamRef.current.getTracks().forEach(track => pcRef.current.addTrack(track, localStreamRef.current));
            if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
        } catch (err) {
            alert("Impossible d'accéder à la caméra/micro : " + err.message);
            throw err;
        }
    }, [wsRef, fromUserId, toUserId, matchId]);

    const startCall = useCallback(async () => {
        await createPeerConnection();
        if (!wsRef?.current || wsRef.current.readyState !== WebSocket.OPEN) return;

        try {
            // Modèle B : envoyer call_request pour notification côté destinataire
            wsRef.current.send(JSON.stringify({
                action: "call_request",
                matchId,
                fromUserId,
                toUserId
            }));
        } catch (err) { console.warn("WS send call_request failed:", err); }
    }, [createPeerConnection, wsRef, matchId, fromUserId, toUserId]);

    const safeAnswerCall = useCallback(async (remoteOffer) => {
        if (!pcRef.current) await createPeerConnection();

        const offerDesc = new RTCSessionDescription(remoteOffer);

        // Attendre que le PC soit stable
        if (pcRef.current.signalingState !== "stable") {
            setTimeout(() => safeAnswerCall(remoteOffer), 100);
            return;
        }

        await pcRef.current.setRemoteDescription(offerDesc);

        const answerDesc = await pcRef.current.createAnswer();
        await pcRef.current.setLocalDescription(answerDesc);

        if (wsRef?.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                action: "call_answer",
                fromUserId,
                toUserId,
                matchId,
                answer: { type: answerDesc.type, sdp: answerDesc.sdp }
            }));
        }

        await drainPendingCandidates();
        setInCall(true);
    }, [createPeerConnection, wsRef, fromUserId, toUserId, matchId, drainPendingCandidates]);

    const stopCall = useCallback(() => {
        setInCall(false);
        try { pcRef.current?.close(); } catch (e) {}
        pcRef.current = null;
        try { localStreamRef.current?.getTracks().forEach(t => t.stop()); } catch (e) {}
        localStreamRef.current = null;
        if (hangupCall) hangupCall(matchId, fromUserId, toUserId);
        onHangup?.();
    }, [hangupCall, matchId, fromUserId, toUserId, onHangup]);

    // toggle camera/mic
    const toggleCamera = () => {
        const t = localStreamRef.current?.getVideoTracks()[0];
        if (!t) return;
        t.enabled = !t.enabled;
        setCameraOn(t.enabled);
    };
    const toggleMic = () => {
        const t = localStreamRef.current?.getAudioTracks()[0];
        if (!t) return;
        t.enabled = !t.enabled;
        setMicOn(t.enabled);
    };

    // --- WebSocket listener ---
    useEffect(() => {
        if (!addCallListener) return;

        const removeListener = addCallListener(async (type, data) => {
            switch (type) {
                case "call_request":
                    // Laisser l'utilisateur accepter/refuser
                    await createPeerConnection();
                    break;
                case "call_offer":
                    await safeAnswerCall(data.offer);
                    break;
                case "call_answer":
                    if (!pcRef.current || pcRef.current.signalingState !== "have-local-offer") return;
                    if (!data?.answer) return;
                    try { await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.answer)); } catch (err) { console.error(err); }
                    await drainPendingCandidates();
                    setInCall(true);
                    break;
                case "call_ice_candidate":
                    if (!data?.candidate) return;
                    if (pcRef.current && pcRef.current.remoteDescription?.type) {
                        try { await pcRef.current.addIceCandidate(data.candidate); } catch (err) { console.warn(err); }
                    } else {
                        pendingCandidatesRef.current.push(data.candidate);
                    }
                    break;
                case "call_hangup":
                    stopCall();
                    break;
                default:
                    break;
            }
        });

        // Auto-answer si offer existante
        if (offer && !inCall) {
            safeAnswerCall(offer);
        }

        // Auto-start si visible et pas d’offre
        if (visible && !offer) {
            startCall().catch(err => console.warn("startCall failed:", err));
        }

        return () => removeListener && removeListener();
    }, [addCallListener, safeAnswerCall, createPeerConnection, drainPendingCandidates, wsRef, offer, inCall, stopCall, startCall, fromUserId, toUserId, matchId, visible]);

    if (!visible) return null;

    return (
        <div
            style={{
                position: "fixed",
                top: position.y,
                left: position.x,
                width: size.width,
                height: size.height,
                background: "#000",
                borderRadius: 10,
                zIndex: 9999,
                boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}
        >
            {/* barre titre */}
            <div
                onMouseDown={startDragging}
                style={{
                    height: 38,
                    background: "#111",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 10px",
                    cursor: "grab",
                    color: "white",
                    userSelect: "none"
                }}
            >
                <span>Visio</span>
                <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => toggleFullscreen()} style={{ background: "#333", color: "white", borderRadius: 4, padding: "4px 8px" }}>⬜</button>
                    <button onClick={stopCall} style={{ background: "red", color: "white", borderRadius: 4, padding: "4px 8px" }}>✖</button>
                </div>
            </div>

            {/* vidéos */}
            <div style={{ position: "relative", flex: 1 }}>
                <video ref={remoteVideoRef} autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <video ref={localVideoRef} autoPlay muted playsInline style={{
                    position: "absolute",
                    bottom: 10,
                    right: 10,
                    width: 150,
                    borderRadius: 8,
                    border: "2px solid white",
                    background: "#000"
                }} />
                {inCall && (
                    <div style={{ position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 12 }}>
                        <button onClick={toggleCamera}>{cameraOn ? "📷" : "🚫"}</button>
                        <button onClick={toggleMic}>{micOn ? "🎙️" : "🔇"}</button>
                    </div>
                )}
            </div>

            {/* resize handle */}
            <div onMouseDown={startResizing} style={{ position: "absolute", bottom: 0, right: 0, width: 18, height: 18, cursor: "nwse-resize", background: "transparent" }} />
        </div>
    );
}
